import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Play, Compass, Camera, Film, ImageOff, ArrowLeftRight } from "lucide-react";
import Photo360Viewer from "@/components/Photo360Viewer";
import Video360Viewer from "@/components/Video360Viewer";
import BeforeAfter360Viewer from "@/components/BeforeAfter360Viewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";

/**
 * Kleines Thumbnail für INTERACTION-Events im Analytics-Session-Detail.
 * Zeigt Poster-Bild bzw. Foto, öffnet beim Klick den passenden Viewer
 * in einem Modal. Fokus-Return + Scroll-Lock werden mitgehandhabt.
 *
 * Datenfluss:
 *  - Media-Info über `GET /api/media/{id}/info` (public, kein Auth nötig).
 *  - Modul-weiter In-Memory-Cache verhindert doppelte Requests bei
 *    wiederholten Media-IDs in derselben Session-Liste.
 *  - IDs mit `src-`-Prefix sind Fallback-Hashes ohne DB-Referenz und
 *    werden gar nicht erst gefetcht (kein 404-Spam).
 */
type MediaInfo = {
    id: string;
    title?: string;
    original_filename?: string;
    media_type?: "photo" | "photo_360" | "video" | "video_360" | "before_after_360";
    poster_url?: string;
    stored_url?: string;
    is_ai_generated?: boolean;
    /** Nur bei `before_after_360`: die zwei referenzierten Media-IDs. */
    before_media_id?: string;
    after_media_id?: string;
};

type CacheEntry =
    | { status: "loading"; promise: Promise<MediaInfo | null> }
    | { status: "ready"; data: MediaInfo | null };

const cache = new Map<string, CacheEntry>();

async function fetchMediaInfo(mediaId: string): Promise<MediaInfo | null> {
    const existing = cache.get(mediaId);
    if (existing?.status === "ready") return existing.data;
    if (existing?.status === "loading") return existing.promise;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const p = (async () => {
        try {
            const res = await fetch(`${base}/api/media/${mediaId}/info`, { credentials: "omit" });
            if (!res.ok) {
                cache.set(mediaId, { status: "ready", data: null });
                return null;
            }
            const data = (await res.json()) as MediaInfo;
            cache.set(mediaId, { status: "ready", data });
            return data;
        } catch {
            cache.set(mediaId, { status: "ready", data: null });
            return null;
        }
    })();
    cache.set(mediaId, { status: "loading", promise: p });
    return p;
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/** Vollansicht-Modal — rendert je nach media_type den passenden Viewer.
 *  Scroll-Lock + Escape + Fokus-Return übernimmt der aufrufende Wrapper. */
function MediaPreviewModal({
    info,
    onClose,
}: {
    info: MediaInfo;
    onClose: () => void;
}) {
    const type = info.media_type || "photo";
    const title = info.title || info.original_filename || "Medium";
    const mediaUrl = `${BACKEND}/api/media/${info.id}`;
    const posterUrl = info.poster_url || "";

    const body =
        type === "before_after_360" ? (
            <ViewerErrorBoundary
                testId="media-preview-modal-ba360-fallback"
                fallbackTitle="Vorher/Nachher 360° kann nicht dargestellt werden"
            >
                {info.before_media_id && info.after_media_id ? (
                    <div style={{ width: "100%", aspectRatio: "16/9", background: "#0e1726" }}>
                        <BeforeAfter360Viewer
                            beforeUrl={`${BACKEND}/api/media/${info.before_media_id}/proxy`}
                            afterUrl={`${BACKEND}/api/media/${info.after_media_id}/proxy`}
                            testIdPrefix="media-preview-modal-ba360"
                            autoTour={false}
                        />
                    </div>
                ) : (
                    <div className="p-6 text-hf-muted text-sm text-center">
                        Vorher/Nachher-Daten unvollständig.
                    </div>
                )}
            </ViewerErrorBoundary>
        ) : type === "photo_360" ? (
            <ViewerErrorBoundary
                testId="media-preview-modal-photo360-fallback"
                fallbackTitle="360°-Foto kann nicht dargestellt werden"
            >
                <div style={{ width: "100%", aspectRatio: "16/9", background: "#0e1726" }}>
                    <Photo360Viewer
                        src={`${BACKEND}/api/media/${info.id}/proxy`}
                        testIdPrefix="media-preview-modal-photo360"
                        mediaId={info.id}
                        mediaTitle={title}
                    />
                </div>
            </ViewerErrorBoundary>
        ) : type === "video_360" ? (
            <ViewerErrorBoundary
                testId="media-preview-modal-video360-fallback"
                fallbackTitle="360°-Video kann nicht gestartet werden"
            >
                <Video360Viewer
                    src={mediaUrl}
                    poster={posterUrl}
                    testIdPrefix="media-preview-modal-video360"
                    autoPlay
                    mediaId={info.id}
                    mediaTitle={title}
                />
            </ViewerErrorBoundary>
        ) : type === "video" ? (
            <video
                src={mediaUrl}
                poster={posterUrl}
                controls
                autoPlay
                className="w-full"
                style={{ maxHeight: "80vh", background: "#000" }}
                data-testid="media-preview-modal-video"
            />
        ) : (
            <img
                src={mediaUrl}
                alt={title}
                className="w-full h-auto"
                style={{ maxHeight: "80vh", objectFit: "contain", background: "hsl(var(--hf-surface))" }}
                data-testid="media-preview-modal-image"
            />
        );

    const modalContent = (
        <div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 animate-in fade-in duration-150"
            style={{
                background: "hsl(var(--hf-ink) / 0.88)",
            }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={onClose}
            data-testid="media-preview-modal"
        >
            <div
                className="relative w-full max-w-6xl rounded-sm overflow-hidden"
                style={{ background: "hsl(var(--hf-bg))" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="flex items-center justify-between px-4 py-2"
                    style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}
                >
                    <div className="min-w-0">
                        <div
                            className="uppercase text-[10px] tracking-[0.2em]"
                            style={{ color: "hsl(var(--hf-gold-dark))" }}
                        >
                            {type === "photo_360" ? "360°-Foto"
                                : type === "video_360" ? "360°-Video"
                                    : type === "before_after_360" ? "Vorher/Nachher · 360°"
                                        : type === "video" ? "Video" : "Foto"}
                        </div>
                        <div
                            className="font-display text-base truncate"
                            title={title}
                            data-testid="media-preview-modal-title"
                        >
                            {title}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-sm hover:bg-hf-surface"
                        aria-label="Schließen"
                        data-testid="media-preview-modal-close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div style={{ background: "hsl(var(--hf-surface))" }}>{body}</div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}

/** Overlay-Icon in der Thumbnail-Ecke — visualisiert den Interaction-Typ. */
function TypeOverlay({ type }: { type?: string }) {
    const icon =
        type === "video_360" ? <Play size={10} fill="currentColor" strokeWidth={0} />
            : type === "photo_360" ? <Compass size={10} strokeWidth={2.2} />
                : type === "before_after_360" ? <ArrowLeftRight size={10} strokeWidth={2.2} />
                    : type === "video" ? <Film size={10} strokeWidth={2.2} />
                        : <Camera size={10} strokeWidth={2.2} />;
    return (
        <span
            aria-hidden
            className="absolute -bottom-1 -right-1 inline-flex items-center justify-center rounded-full"
            style={{
                width: 16,
                height: 16,
                background: "hsl(var(--hf-gold-dark))",
                color: "hsl(var(--hf-bg))",
                border: "1.5px solid hsl(var(--hf-bg))",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            }}
        >
            {icon}
        </span>
    );
}

type Props = {
    mediaId: string;
    /** Optional aus custom_data.viewer_type (video360 / photo360). */
    viewerTypeHint?: string;
    /** Optional aus custom_data.media_title (kann Fallback sein). */
    titleHint?: string;
    /** Fallback-Größe der Thumbnail. Default 40. */
    size?: number;
    testIdPrefix?: string;
};

/** Ist die media_id eine echte DB-UUID (nicht ein `src-hash`-Fallback)? */
function isRealMediaId(id: string): boolean {
    if (!id) return false;
    if (id.startsWith("src-")) return false;
    return true;
}

export default function MediaInteractionThumbnail({
    mediaId,
    viewerTypeHint,
    titleHint,
    size = 40,
    testIdPrefix = "session-media-thumb",
}: Props) {
    const realId = isRealMediaId(mediaId);
    const initialCached = realId ? cache.get(mediaId) : undefined;
    const initialData =
        initialCached?.status === "ready" ? initialCached.data : undefined;
    const [info, setInfo] = useState<MediaInfo | null | undefined>(initialData);
    const [modalOpen, setModalOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (!realId) return;
        let cancelled = false;
        void (async () => {
            const data = await fetchMediaInfo(mediaId);
            if (!cancelled) setInfo(data);
        })();
        return () => { cancelled = true; };
    }, [mediaId, realId]);

    // Scroll-Lock + Escape + Fokus-Return beim Modal.
    useEffect(() => {
        if (!modalOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setModalOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKey);
            // Fokus zurück auf den Auslöser-Button (Accessibility).
            btnRef.current?.focus();
        };
    }, [modalOpen]);

    // Fallback-ID (`src-...`, leer, null) oder DB-Eintrag entfernt → grauer Placeholder.
    if (!realId || info === null) {
        const reason = !mediaId
            ? "Kein Media-Bezug (aus früherem Tracking)"
            : !realId
                ? "Kein DB-Bezug (Fallback-Hash)"
                : "Medium entfernt";
        return (
            <span
                className="inline-flex items-center justify-center rounded-sm"
                style={{
                    width: size,
                    height: size,
                    background: "hsl(var(--hf-surface))",
                    border: "1px dashed #D8CDBF",
                    color: "hsl(var(--hf-muted))",
                    flex: "0 0 auto",
                }}
                title={reason}
                data-testid={`${testIdPrefix}-placeholder`}
                aria-label={reason}
            >
                <ImageOff size={14} strokeWidth={1.8} />
            </span>
        );
    }

    // Noch am Laden — Skeleton.
    if (info === undefined) {
        return (
            <span
                className="inline-block rounded-sm"
                style={{
                    width: size,
                    height: size,
                    background: "hsl(var(--hf-surface))",
                    border: "1px solid #D8CDBF",
                    flex: "0 0 auto",
                }}
                aria-hidden
                data-testid={`${testIdPrefix}-loading`}
            />
        );
    }

    const type = info.media_type || (viewerTypeHint === "video360" ? "video_360"
        : viewerTypeHint === "photo360" ? "photo_360"
            : viewerTypeHint === "before_after_360" ? "before_after_360"
                : "photo");
    const isVideo = type === "video" || type === "video_360";
    const thumbSrc = isVideo
        ? info.poster_url || ""
        : `${BACKEND}/api/media/${info.id}`;

    const title = info.title || info.original_filename || titleHint || "Medium";

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={() => setModalOpen(true)}
                className="relative inline-flex items-center justify-center rounded-sm overflow-hidden group focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hf-gold-dark))]"
                style={{
                    width: size,
                    height: size,
                    background: "hsl(var(--hf-surface))",
                    border: "1px solid #D8CDBF",
                    flex: "0 0 auto",
                    cursor: "pointer",
                    transition: "border-color 120ms ease, transform 120ms ease",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "hsl(var(--hf-gold-dark))";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#D8CDBF";
                }}
                title={`${title} — Klick zum Öffnen`}
                aria-label={`${title} im Vorschau-Modus öffnen`}
                data-testid={`${testIdPrefix}-${info.id}`}
            >
                {thumbSrc ? (
                    <img
                        src={thumbSrc}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <span
                        className="inline-flex items-center justify-center w-full h-full"
                        style={{ color: "hsl(var(--hf-muted))" }}
                    >
                        <Film size={16} />
                    </span>
                )}
                <TypeOverlay type={type} />
            </button>
            {modalOpen && (
                <MediaPreviewModal
                    info={info}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </>
    );
}
